import { DynamicForm } from "@shared/components/DynamicForm";
import { PhoneInputComponent } from "@shared/components/PhoneInputComponent";
import { toast } from "@shared/hooks/use-toast";
import { useCountry, useTenantId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  locationJsonService,
  LocationOption,
} from "@shared/services/locationService";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Building, Loader2, Upload, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useCreateUtilityMutation, useUpdateUtility } from "../../hooks";

interface UtilityAddFormProps {
  onCancel: () => void;
  mode?: "add" | "edit";
  utility?: any; // Complete utility object for edit mode
}

const UtilityAddForm: React.FC<UtilityAddFormProps> = ({
  onCancel,
  mode = "add",
  utility, // Complete utility object
}) => {
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [stateOptions, setStateOptions] = useState<LocationOption[]>([]);
  const [cityOptions, setCityOptions] = useState<LocationOption[]>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [selectedState, setSelectedState] = useState<string>("");

  const tenantID = useTenantId();
  const countryCode = useCountry() ?? "US";
  const createUtilityMutation = useCreateUtilityMutation();
  const updateUtilityMutation = useUpdateUtility();
  const isLoading =
    createUtilityMutation.isPending || updateUtilityMutation.isPending;

  // Optimized function to load cities with caching
  const loadCitiesForState = useCallback(
    async (stateValue: string): Promise<LocationOption[]> => {
      if (!stateValue || !countryCode) {
        setCityOptions([]);
        return [];
      }

      setIsCitiesLoading(true);
      try {
        // Use requestAnimationFrame for better performance
        const cities = await new Promise<LocationOption[]>((resolve) => {
          requestAnimationFrame(() => {
            const result = locationJsonService.getCitiesByState(
              countryCode,
              stateValue,
            );
            resolve(result);
          });
        });

        setCityOptions(cities);
        console.log(
          "Loaded cities for state:",
          stateValue,
          cities.length,
          "cities",
        );
        return cities;
      } catch (error) {
        console.error("Error loading cities:", error);
        setCityOptions([]);
        return [];
      } finally {
        setIsCitiesLoading(false);
      }
    },
    [countryCode],
  );

  // Get dynamic placeholder and disabled state for city field
  const getCityFieldProps = useCallback(() => {
    if (isCitiesLoading) {
      return {
        placeholder: "Loading cities...",
        disabled: true,
        className: "cursor-wait",
      };
    }

    if (!selectedState) {
      return {
        placeholder: "Select a state first",
        disabled: true,
        className: "cursor-not-allowed",
      };
    }

    if (cityOptions.length === 0) {
      return {
        placeholder: "No cities available",
        disabled: true,
        className: "cursor-not-allowed",
      };
    }

    return {
      placeholder: "Select a city",
      disabled: false,
      className: "bg-white cursor-pointer",
    };
  }, [isCitiesLoading, selectedState, cityOptions.length]);

  // Form fields definition with improved city field
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Utility Name",
      required: true,
      placeholder: "Enter utility name",
      minLength: 3,
      fullWidth: true,
      helperText:
        "The official name of your utility company as it appears on documents",

      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "email",
      type: "email",
      label: "Email",
      required: true,
      placeholder: "contact@example.com",
      pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
      fullWidth: true,
      helperText:
        "Primary contact email for customer service and business communications",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },

    {
      name: "contact_number",
      type: "phone",
      label: "Contact Number",
      required: true,
      placeholder: "Enter phone number",
      fullWidth: true,
      helperText: "Main phone number for customer inquiries and support",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
      customRender: (field, form, error) => (
        <PhoneInputComponent
          value={form.getValues(field.name)}
          onChange={(val: any) => form.setValue(field.name, val)}
          placeholder={field.placeholder}
          className={error ? "border-red-500" : ""}
        />
      ),
    },
    {
      name: "state",
      type: "select",
      label: "State",
      helperText: "State where your utility is primarily headquartered",
      required: true,
      placeholder:
        stateOptions.length > 0 ? "Select a state" : "Loading states...",
      fullWidth: true,
      options: stateOptions,
      disabled: stateOptions.length === 0,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 ${
          stateOptions.length === 0
            ? "bg-gray-50 cursor-not-allowed"
            : "bg-white cursor-pointer"
        }`,
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "city",
      type: "select",
      label: "City",
      required: true,
      ...getCityFieldProps(),
      fullWidth: true,
      options: cityOptions,
      helperText: "Primary city where your utility operates from",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 ${
          getCityFieldProps().className
        }`,
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "gst_hst_number",
      type: "text",
      label: "GST/HST/VAGST Registration",
      required: false,
      placeholder: "Enter registration number",
      fullWidth: true,
      helperText: "Tax registration number (if applicable)",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "address",
      type: "textarea",
      label: "Address",
      required: true,
      placeholder: "Enter full address",
      fullWidth: true,
      rows: 2,
      helperText:
        "Complete physical address including street, city, state, and ZIP code",
      classes: {
        container: "w-full mb-4 col-span-2",
        label: "block text-sm font-medium text-gray-700 mb-2",
        textarea:
          "w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white resize-none",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      showHelperTooltip: true,
      required: true,
      placeholder: "Enter description",
      fullWidth: true,
      helperText:
        "Optional description of your utility company, services offered, or mission statement",

      classes: {
        container: "w-full mb-4 col-span-2",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
        textarea:
          "w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white resize-none",
      },
    },
    {
      name: "website",
      type: "url",
      label: "Website URL",
      required: false,
      placeholder: "https://www.example.com",
      fullWidth: true,
      helperText: "Official website URL for customer portal and information",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "logo",
      type: "file",
      label: "Logo Upload",
      required: false,
      fullWidth: true,
      helperText: "Upload your company logo (JPEG, PNG, GIF)",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        file: "w-full",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
      customRender: () => <LogoUploadComponent />,
    },
  ];

  // Create form
  const orgSetupForm = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Load states on country change
  useEffect(() => {
    if (countryCode) {
      const states = locationJsonService.getStatesByCountry(countryCode);
      setStateOptions(states);
      console.log("Loaded states:", states.length, "states");
    }
  }, [countryCode]);

  // Initialize form data and locations
  useEffect(() => {
    const initializeForm = async () => {
      const data = utility;
      console.log("Initializing form with data:", data);

      if (!data || mode === "add") return;

      setIsInitializing(true);

      try {
        const stateValue = data.addressMap?.state || "";
        const cityValue = data.addressMap?.city || "";

        console.log("Extracted state:", stateValue, "city:", cityValue);

        // Set selected state immediately
        setSelectedState(stateValue);

        // Set logo preview if exists
        const logoUrl = data.logoPreview || data.logo || data.LogoUrl || "";
        if (logoUrl) {
          setLogoPreview(logoUrl);
        }
        console.log("data", data);
        // First, reset form with basic data (without city)
        const formData = {
          name: data.name || "",
          email: data.email || "",
          contact_number: data.contactNumber || "",
          description: data.description || "",
          state: stateValue,
          city: "", // Set empty initially
          gst_hst_number: data.gstHstNumber || "",
          address: data.addressMap?.address || "",
          website: data.website || "",
          logo: null,
        };

        console.log("Setting initial form data:", formData);
        orgSetupForm.reset(formData);

        // Load cities if state exists, then set city
        if (stateValue && countryCode) {
          console.log("Loading cities for state:", stateValue);
          const citiesToUse = await loadCitiesForState(stateValue);

          // After cities are loaded, set the city value
          if (cityValue && citiesToUse.length > 0) {
            const cityExists = citiesToUse.some(
              (city) => city.value === cityValue,
            );
            console.log("City exists in options:", cityExists, cityValue);

            if (cityExists) {
              // Use setTimeout to ensure the form field is ready
              setTimeout(() => {
                orgSetupForm.setValue("city", cityValue);
                console.log("City value set to:", cityValue);
              }, 100);
            } else {
              console.warn("City not found in options:", cityValue);
            }
          }
        }
      } catch (error) {
        console.error("Error initializing form:", error);
      } finally {
        setIsInitializing(false);
      }
    };

    if (stateOptions.length > 0 || mode === "add") {
      initializeForm();
    }
  }, [
    utility,
    stateOptions,
    countryCode,
    mode,
    orgSetupForm,
    loadCitiesForState,
  ]);

  // Consolidated field change handler
  const handleFieldChange = useCallback(
    async (fieldName: string, value: any) => {
      // Clear field errors
      if (FormService.getFieldError(orgSetupForm, fieldName)) {
        orgSetupForm.clearErrors(fieldName);
      }

      // Handle state change with smooth city transition
      if (fieldName === "state" && value !== selectedState) {
        console.log("State changed from", selectedState, "to", value);
        setSelectedState(value);

        // Clear city value immediately for better UX
        const currentCity = orgSetupForm.getValues("city");

        if (value) {
          // Don't clear city immediately, let user see the loading state
          try {
            const cities = await loadCitiesForState(value);

            // Check if the current city exists in the new state
            const cityExists = cities.some(
              (city) => city.value === currentCity,
            );

            if (!cityExists) {
              // Only clear city if it doesn't exist in new state
              orgSetupForm.setValue("city", "");
            }
          } catch (error) {
            console.error("Error handling state change:", error);
            orgSetupForm.setValue("city", "");
          }
        } else {
          // Clear city when no state is selected
          orgSetupForm.setValue("city", "");
          setCityOptions([]);
        }
      }
    },
    [selectedState, orgSetupForm, loadCitiesForState],
  );

  // Handle logo upload
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
    ];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please select a valid image file (JPEG, PNG, or GIF)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image size should not exceed 2MB",
        variant: "destructive",
      });
      return;
    }

    setLogo(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (evt) => {
      setLogoPreview(evt.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Update form
    orgSetupForm.setValue("logo", file);
    orgSetupForm.clearErrors("logo");
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
    orgSetupForm.setValue("logo", null);
  };

  // Logo upload component
  const LogoUploadComponent = () => (
    <div className="w-full">
      <div
        className={`border rounded-lg p-3 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors h-[74px] ${
          logoPreview ? "border-primary/30 bg-primary/5" : "border-dashed"
        } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() =>
          !isLoading && document.getElementById("logo-upload")?.click()
        }
      >
        {logoPreview ? (
          <div className="relative w-full flex items-center justify-center">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="absolute -top-1 -right-1 rounded-full h-6 w-6 p-0"
              disabled={isLoading}
              onClick={(e) => {
                e.stopPropagation();
                removeLogo();
              }}
            >
              <X className="h-3 w-3" />
            </Button>
            <img
              src={logoPreview}
              alt="Logo Preview"
              className="mx-auto max-h-12 object-contain"
            />
            <p className="text-xs text-center mt-1 text-muted-foreground">
              {logo?.name && logo.name.length > 15
                ? logo.name.substring(0, 15) + "..."
                : logo?.name || "Existing logo"}
              {logo?.size && ` (${(logo.size / 1024).toFixed(1)} KB)`}
            </p>
          </div>
        ) : (
          <div className="text-center">
            <Upload className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">
              Click to upload logo
            </p>
          </div>
        )}
        <input
          id="logo-upload"
          type="file"
          accept="image/png,image/jpeg,image/gif"
          className="hidden"
          disabled={isLoading}
          onChange={handleLogoChange}
        />
      </div>
    </div>
  );

  // Handle form submission
  const handleSubmit = async (data: any) => {
    console.log("Submitting form data:", data);
    try {
      const isValid = await FormService.validateForm(orgSetupForm);

      if (!isValid) {
        return;
      }

      let payload = FormService.createPayload(data, formFields);
      console.log("Form payload:", payload);

      const formData = new FormData();

      // Add required fields
      formData.append("name", data.name);
      formData.append("email", data.email);
      formData.append("contact_number", data.contact_number);
      formData.append("description", data.description);

      formData.append("short_name", data.name);
      formData.append("tenant", tenantID);
      formData.append("status", "x");

      // Add optional fields
      if (data.website) formData.append("website", data.website);
      if (data.gst_hst_number)
        formData.append("gst_hst_number", data.gst_hst_number);

      const { city = "", state = "", address = "" } = data;
      formData.append("address_map", JSON.stringify({ state, city, address }));

      // Add logo if it's a new file
      if (logo instanceof File) {
        formData.append("logo", logo);
      }

      if (mode === "edit") {
        console.log("Updating utility with ID:", utility?.id);
        await updateUtilityMutation.mutateAsync({
          formData,
          id: utility?.id,
        });
      } else {
        await createUtilityMutation.mutateAsync(formData);
      }

      toast({
        title: "Success",
        description: `Utility ${
          mode === "edit" ? "updated" : "created"
        } successfully!`,
      });

      onCancel();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Custom footer
  const renderCustomFooter = () => (
    <CardFooter className="flex justify-between gap-3 p-4 pt-2 border-t">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onCancel}
        disabled={isLoading}
      >
        Cancel
      </Button>
      <Button size="sm" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {mode === "edit" ? "Saving..." : "Creating..."}
          </>
        ) : mode === "edit" ? (
          "Save Changes"
        ) : (
          "Save Utility"
        )}
      </Button>
    </CardFooter>
  );

  return (
    <Card className="max-w-2xl p-0 flex flex-col max-h-[90vh] overflow-y-scroll">
      <CardHeader className="p-4 pb-2 space-y-1">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-primary/10">
            <Building className="h-4 w-4 text-primary" />
          </div>
          <CardTitle className="text-lg">
            {mode === "edit" ? "Edit Utility" : "Add New Utility"}
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent className="p-4">
        <DynamicForm
          fields={formFields}
          form={orgSetupForm}
          onSubmit={handleSubmit}
          onFieldChange={handleFieldChange}
          loading={isLoading || isInitializing}
          gridColumns={2}
          responsiveColumns={{ sm: 1, md: 2 }}
          config={{
            showProgress: false,
            layout: "vertical",
            spacing: "none",
            submitButtonText: mode === "edit" ? "Save Changes" : "Save Utility",
            showResetButton: false,
            resetOnSubmit: false,
            classes: {
              form: "space-y-0",
              fieldContainer: "",
              buttonContainer: "hidden",
              fieldGroup: "",
            },
          }}
          renderCustomFooter={renderCustomFooter}
          style={{
            background: "transparent",
            border: "none",
            boxShadow: "none",
          }}
        />
      </CardContent>
    </Card>
  );
};

export default UtilityAddForm;
